import { useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { JobAssignmentSummary } from './JobAssignmentSummary.js';

interface AcceptResult {
  readonly assignmentId: string;
  readonly status: string;
  readonly respondedAt: string;
}

/** §9 Job Acceptance — POST /v1/vendor/assignments/:assignmentId/accept {remarks?}. */
export function VendorJobAcceptPage(): ReactNode {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [remarks, setRemarks] = useState('');
  const [result, setResult] = useState<AcceptResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function accept() {
    setError(null);
    setBusy(true);
    try {
      const body: { remarks?: string } = {};
      if (remarks.trim()) body.remarks = remarks.trim();
      const r = await apiRequest<AcceptResult>(`/v1/vendor/assignments/${assignmentId}/accept`, {
        method: 'POST',
        token,
        body,
      });
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="vendor-job-accept">
      <h1>{t('vops.jobAccept.title')}</h1>
      <p><Link to={`/vendor-ops/jobs/${assignmentId}`} aria-label="back-to-job">{t('vops.common.backToJob')}</Link></p>

      <JobAssignmentSummary assignmentId={assignmentId} />

      {error ? <p role="alert">{error}</p> : null}
      {result ? (
        <p role="status" aria-label="accept-result">{t('vops.jobAccept.result', { status: result.status })}</p>
      ) : null}

      <label htmlFor="accept-remarks">{t('vops.common.remarksOptional')}</label>
      <textarea
        id="accept-remarks"
        aria-label="accept-remarks"
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        disabled={busy || result !== null}
      />

      <Button
        type="button"
        aria-label="accept-btn"
        onClick={accept}
        disabled={busy || result !== null}
        variant="secondary"
      >
        {t('vops.jobAccept.acceptBtn')}
      </Button>
    </main>
  );
}
